import { DeckShareActivityCard, DeckShareDetailsCard } from '@/components/deck-shares';
import type { DeckShare } from '@/config/objects/deck-shares';
import {
  Breadcrumbs,
  Button,
  ConfirmDialog,
  DetailPageHeader,
  DetailPageHero,
  PageContainer,
  StatusPill,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Presentation, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const LIST_PATH = '/presentation-shares';

/**
 * Detail view for one presentation share: the link to send, and the log of when
 * the recipient opened it.
 */
export function DeckShareDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data, isLoading } = useQuery<{ success: boolean; data: DeckShare }>({
    queryKey: ['DeckShare', id],
    queryFn: () => apiClient.get(`/deck-shares/${id}`),
    enabled: !!id,
  });
  const share = data?.data;

  const deleteMutation = useMutation({
    mutationFn: async (): Promise<{ success: boolean; error?: string }> =>
      apiClient.delete(`/deck-shares/${id}`),
    onSuccess: (response) => {
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete share');
      }
      queryClient.invalidateQueries({ queryKey: ['DeckShare'] });
      navigate(LIST_PATH);
    },
  });

  const handleDelete = () => {
    toast.promise(deleteMutation.mutateAsync(), {
      loading: 'Deleting share...',
      success: 'Share deleted',
      error: (error: Error) => error?.message || 'Failed to delete share',
    });
  };

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Presentation Shares', href: LIST_PATH },
    { label: share?.recipientName || 'Share', current: true },
  ];

  return (
    <PageContainer padding="none" className="flex flex-col overflow-hidden">
      <DetailPageHeader
        actions={
          share ? (
            <>
              <Button variant="outline" onClick={() => navigate(`${LIST_PATH}/${id}/edit`)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          ) : undefined
        }
      >
        <Breadcrumbs items={breadcrumbs} />
      </DetailPageHeader>

      <DetailPageHero
        isLoading={isLoading}
        category={{ label: 'Presentation Share', icon: Presentation }}
        title={share?.recipientName || 'Share'}
        image={{
          url: null,
          alt: share?.recipientName || 'Share',
          fallbackIcon: Presentation,
        }}
        meta={share?.hotel?.name ? <span>{share.hotel.name}</span> : undefined}
        badges={
          share ? (
            <>
              <StatusPill
                status={share.isActive && !share.revokedAt ? 'published' : 'needs_review'}
                label={share.revokedAt ? 'Revoked' : share.isActive ? 'Live' : 'Switched off'}
              />
              <StatusPill
                status={share.viewCount > 0 ? 'published' : 'draft'}
                label={
                  share.viewCount > 0
                    ? `${share.viewCount} open${share.viewCount === 1 ? '' : 's'}`
                    : 'Not opened'
                }
              />
            </>
          ) : undefined
        }
      >
        {share && (
          <div className="space-y-6">
            <DeckShareDetailsCard share={share} />
            <DeckShareActivityCard share={share} />
          </div>
        )}
      </DetailPageHero>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Share"
        description={`Delete the presentation link for "${share?.recipientName}"? The link stops working immediately and its view history is removed.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </PageContainer>
  );
}
